import PageHeader from "../components/PageHeader";
import { Diamond, Heart, ShieldCheck, Sparkles } from "lucide-react";

const VALUES = [
  { icon: Diamond, title: "Refinement", text: "Every detail — from the linens to the lighting — is curated to a quiet, exacting standard." },
  { icon: Heart, title: "Hospitality", text: "We treat every guest as a personal acquaintance and every owner as a long-term partner." },
  { icon: ShieldCheck, title: "Trust", text: "Transparent reporting, vetted guests, and a single point of contact for every owner." },
  { icon: Sparkles, title: "Performance", text: "Hospitality with hard numbers — yield, occupancy and reviews, tracked obsessively." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#050505] text-white -mb-24">
      <PageHeader
        overline="Our Story"
        title="A London-born studio for luxury short letting."
        subtitle="We exist at the intersection of hotel-grade hospitality and the intimacy of a private residence — quietly elevating short-term living since 2012."
        image="https://images.pexels.com/photos/14765824/pexels-photo-14765824.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600"
      />

      <section className="py-24">
        <div className="container mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="overline mb-5">The Philosophy</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">A residence, not a rental.</h2>
          </div>
          <div className="md:col-span-7">
            <div className="space-y-6 text-white/70 leading-relaxed text-base sm:text-lg">
              <p>
                West London Stays began with a simple observation: London's most beautiful homes
                were being managed like commodities. We set out to build a studio — small, opinionated, deeply
                hands-on — that would treat every property as a residence and every stay as theatre.
              </p>
              <p>
                Today, our portfolio spans Belgravia townhouses, Mayfair apartments and Notting Hill ateliers.
                Behind each address is a senior account manager, a 24/7 concierge desk, and a logistics
                team trained to hotel standards.
              </p>
              <p>
                Our owners stay because the numbers work. Our guests return because the experience does.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0a0d0b]">
        <div className="container mx-auto px-6 md:px-12">
          <p className="overline mb-5">Mission &amp; Values</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight max-w-3xl mb-16">
            To elevate every short-term stay into a residence-grade experience.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <div key={i} data-testid={`value-card-${i}`} className="card-flat p-8">
                <div className="w-12 h-12 border border-gold/40 flex items-center justify-center mb-6 text-gold">
                  <v.icon size={20} />
                </div>
                <h3 className="font-serif text-xl text-white mb-3">{v.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="image-zoom aspect-[4/5]">
            <img src="https://images.unsplash.com/photo-1713832139688-79676097edde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="overline mb-5">Our Approach</p>
            <h3 className="font-serif text-4xl text-white mb-6 leading-tight">Professional. Reliable. Quietly extraordinary.</h3>
            <div className="space-y-4 text-white/65 leading-relaxed">
              <p>We operate with the discipline of a hotel and the warmth of a family business. Every property gets a senior owner of its account, a documented playbook, and quarterly performance reviews.</p>
              <p>We invest in our team because hospitality is, ultimately, a human craft. From housekeepers to senior concierge, every member is hand-picked, trained and retained.</p>
            </div>
            <div className="gold-divider mt-10" />
          </div>
        </div>
      </section>
    </div>
  );
}
